import {observer} from 'mobx-react-lite'
import React, {useState} from 'react'
import {Lang} from 'yougile-lang'
import {
  GoogleCalendarIntegrationsStatus,
  GoogleCalendarSettings
} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {
  TwFooter,
  TwForm,
  TwFormInputError,
  TwRow,
  TwWarningBlock,
  TwWarningDescription,
  TwWarningSign,
  TwWarningText,
  TwWarningTitle
} from '@components/account/profile/google-calendar-settings-popup/containers'
import {
  getBoardsByCompanies,
  useGoogleCalendarSettingsForm
} from '@components/account/profile/google-calendar-settings-popup/use-google-calendar-settings-form'
import {ButtonNew} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {Popup} from '@components/common/base/popup'
import {RadioButton} from '@components/common/base/radiobutton'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {TreeList} from '@components/common/base/tree-list'

export const GoogleCalendarSettingsPopup = observer(() => {
  const [loading, setLoading] = useState(false)
  const [popupStatus, setPopupStatus] = useState<
    'active' | 'warning' | 'success'
  >('active')
  const {
    formData,
    formIsChanged,
    validate,
    close,
    googleCalendarStatus,
    boardsCompanyMap,
    setGoogleSettings,
    resetGoogleSettings
  } = useGoogleCalendarSettingsForm()

  const onClose = () => {
    if (googleCalendarStatus === 'active' || googleCalendarStatus === 'off') {
      close()
    }

    setPopupStatus('warning')
  }

  const resetSync = async () => {
    setLoading(true)
    void Tracking.send(
      TrackingEvent.googleCalendarClickOnDisableGoogleIntegrationButtonInSettingsPopup
    )
    await resetGoogleSettings()
  }

  const setSync = async () => {
    if (!validate()) {
      const showSuccess = googleCalendarStatus === 'isReady'
      setLoading(true)
      const googleSettings: GoogleCalendarSettings = {
        ...formData
      }
      if (formData.boards) {
        googleSettings.companiesBoards = getBoardsByCompanies(
          formData.boards,
          boardsCompanyMap
        )
      }

      const success = await setGoogleSettings(googleSettings, formIsChanged)

      if (success) {
        void Tracking.send(
          TrackingEvent.googleCalendarSuccessGoogleIntegrationPopup
        )
        if (showSuccess) {
          setPopupStatus('success')
          setLoading(false)
        } else {
          close()
        }
      } else {
        setLoading(false)
      }
    }
  }

  return (
    <Popup
      autoHeight
      width={400}
      title={Lang.msg.googleCalendarIntegration}
      close={onClose}
      noCross={popupStatus === 'warning'}
      isLoading={loading}
      footer={
        <TwFooter>
          {popupStatus === 'success' && <SuccessFooter onClose={onClose} />}
          {popupStatus === 'warning' && (
            <WarningFooter
              setPopupStatus={setPopupStatus}
              resetSync={resetSync}
            />
          )}
          {popupStatus !== 'success' && popupStatus !== 'warning' && (
            <DefaultFooter
              googleCalendarStatus={googleCalendarStatus}
              resetSync={resetSync}
              setSync={setSync}
              formIsChanged={formIsChanged}
              onClose={onClose}
            />
          )}
        </TwFooter>
      }
    >
      {popupStatus === 'success' && <SuccessContent />}
      {popupStatus === 'warning' && <WarningContent />}
      {popupStatus !== 'success' && popupStatus !== 'warning' && (
        <DefaultContent />
      )}
    </Popup>
  )
})

const SuccessFooter = ({onClose}: {onClose: () => void}) => {
  return (
    <TwFooter>
      <ButtonNew color="primary" size="md" onClick={onClose}>
        {Lang.msg.close}
      </ButtonNew>
    </TwFooter>
  )
}

const WarningFooter = ({
  setPopupStatus,
  resetSync
}: {
  setPopupStatus: React.Dispatch<
    React.SetStateAction<'active' | 'warning' | 'success'>
  >
  resetSync: () => void
}) => {
  return (
    <>
      <ButtonNew
        color="primary"
        size="md"
        onClick={() => {
          setPopupStatus('active')
        }}
      >
        {Lang.msg.common.setup}
      </ButtonNew>
      <ButtonNew color="notification-primary" size="md" onClick={resetSync}>
        {Lang.msg.common.cancelSync}
      </ButtonNew>
    </>
  )
}

const DefaultFooter = ({
  onClose,
  resetSync,
  googleCalendarStatus,
  formIsChanged,
  setSync
}: {
  onClose: () => void
  resetSync: () => void
  googleCalendarStatus?: GoogleCalendarIntegrationsStatus | 'error'
  formIsChanged: boolean
  setSync: () => void
}) => {
  return (
    <>
      <ButtonNew color="secondary" size="md" onClick={onClose}>
        {Lang.msg.cancel}
      </ButtonNew>
      {googleCalendarStatus === 'active' && (
        <>
          <ButtonNew color="notification-primary" size="md" onClick={resetSync}>
            {Lang.msg.common.disable}
          </ButtonNew>
          <ButtonNew disabled={!formIsChanged} size="md" onClick={setSync}>
            {Lang.msg.save}
          </ButtonNew>
        </>
      )}
      {googleCalendarStatus !== 'active' && (
        <ButtonNew size="md" onClick={setSync}>
          {Lang.msg.common.enableSync}
        </ButtonNew>
      )}
    </>
  )
}

const SuccessContent = () => {
  return (
    <TwWarningBlock>
      <TwWarningSign $success>
        <Icon
          icon="IconCustomSelectTaskAnySize"
          size={32}
          color="generic-white"
        />
        {Lang.msg.done}
      </TwWarningSign>
      <TwWarningDescription>
        <TwWarningTitle>{Lang.msg.googleCalendar.tasksAdded}</TwWarningTitle>
        <TwWarningText>
          {Lang.msg.googleCalendar.allNewWillBeSent}
        </TwWarningText>
      </TwWarningDescription>
    </TwWarningBlock>
  )
}

const WarningContent = () => {
  return (
    <TwWarningBlock>
      <TwWarningSign>
        <Icon icon="IconCustomCloseAnySize" size={32} color="generic-white" />
        {Lang.msg.googleCalendar.notConfigured}
      </TwWarningSign>
      <TwWarningDescription>
        <TwWarningTitle>
          {Lang.msg.googleCalendar.synchronizationIsNotSet}
        </TwWarningTitle>
        <TwWarningText>{Lang.msg.googleCalendar.whatWillBeSent}</TwWarningText>
      </TwWarningDescription>
    </TwWarningBlock>
  )
}

const DefaultContent = observer(() => {
  const {
    formData,
    formErrors,
    calendarValueTitle,
    boardsProjectsTitle,
    calendarOptions,
    boardsList,
    updateForm,
    onSelectBoards
  } = useGoogleCalendarSettingsForm()

  return (
    <TwForm>
      <TwRow>
        <div className="text-secondary">{Lang.msg.chooseGoogleAccount}</div>
        <SelectWithDropdown disabled variant="new" value={formData.email} />
      </TwRow>
      <TwRow>
        <div className="text-secondary">{Lang.msg.chooseCalendar}</div>
        <SelectWithDropdown
          variant="new"
          error={Boolean(formErrors.calendar?.length)}
          disabled={Boolean(
            calendarValueTitle && calendarOptions?.length === 1
          )}
          value={calendarValueTitle}
          placeholder={Lang.msg.chooseCalendar}
          options={calendarOptions}
          onChange={option => updateForm({calendar: option.key})}
        />
        {formErrors.calendar?.map(error => (
          <TwFormInputError key={error}>
            {Lang.tryLocalize(error)}
          </TwFormInputError>
        ))}
      </TwRow>
      {!!boardsList?.length && (
        <TwRow>
          <div className="text-secondary">{Lang.msg.chooseBoardProject}</div>
          <SelectWithDropdown
            variant="new"
            error={Boolean(formErrors.boards?.length)}
            value={boardsProjectsTitle}
            placeholder={Lang.msg.chooseBoardProject}
            content={
              <TreeList
                withSearch
                firstCollapsed
                className="w-[352px]"
                list={boardsList}
                collapsedLocalStorageKey="google-calendar-settings-collapsed-projects"
                onSelect={onSelectBoards}
                selected={formData.boards}
              />
            }
          />
          {formErrors.boards?.map(error => (
            <TwFormInputError key={error}>
              {Lang.tryLocalize(error)}
            </TwFormInputError>
          ))}
        </TwRow>
      )}
      <TwRow className="py-8">
        <RadioButton
          active={
            formData.tasksType !== undefined
              ? formData.tasksType === 'my'
              : true
          }
          onClick={() => updateForm({tasksType: 'my'})}
          label={Lang.msg.googleCalendar.onlyMyTasks}
        />
        <RadioButton
          active={formData.tasksType === 'all'}
          onClick={() => updateForm({tasksType: 'all'})}
          label={Lang.msg.googleCalendar.allTasks}
        />
      </TwRow>
    </TwForm>
  )
})
